import { Helmet } from 'react-helmet-async';
import PageLayout from '../../components/PageLayout';
import { Phone, MapPin, Wrench, CheckCircle2 } from 'lucide-react';

const IrittyLanding = () => {
  return (
    <>
      <Helmet>
        <title>House Construction in Iritty | Master Home Builders</title>
        <meta name="description" content="Looking for trusted house construction in Iritty? Master Home offers premium turnkey building, remodeling, and modern designs tailored for you." />
        <link rel="canonical" href="https://masterhome.co.in/locations/iritty" />
      </Helmet>

      <PageLayout
        title="House Construction in Iritty, Kerala"
        subtitle="Expert local builders transforming your vision into concrete reality right here in Iritty."
      >
        <div className="space-y-16">
          {/* Introduction */}
          <section className="glass-card p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6 text-gradient">Your Neighborhood Civil Contractors in Iritty</h2>
            <div className="prose max-w-none text-gray-600 text-lg leading-relaxed">
              <p className="mb-4">
                Planning a house construction in Iritty requires a builder who deeply understands the local terrain, material availability, and community sensibilities. Whether you are an NRI looking to invest in a gorgeous retirement villa or a young family building your first home near the town center, coordinating multiple vendors from afar is a major headache.
              </p>
              <p>
                Master Home removes the friction from custom home construction. Based near the Iritty Taluk Co-op Society, we are your steadfast local partners. We handle everything—from acquiring panchayat approvals to laying the final vitrified tiles—so you don't have to face the typical stresses of budget leaks and unreliable labor.
              </p>
            </div>
          </section>

          {/* Local Proof */}
          <section className="grid md:grid-cols-2 gap-8 items-center">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 lg:order-2">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="text-primary-600" /> Iritty & Surrounding Zones
              </h3>
              <ul className="space-y-3">
                {['Keezhur', 'Payam', 'Ulikkal', 'Mattannur nearby', 'Padiyoor'].map((area) => (
                  <li key={area} className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-primary-500" />
                    Turnkey Construction in {area}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-6 lg:pl-8 lg:order-1">
              <h2 className="text-3xl font-bold text-gray-900">Designed for Iritty's Landscape</h2>
              <p className="text-gray-600 text-lg">
                The lush, hilly topography around Iritty and the Valapattanam River basin requires houses with robust foundations and excellent soil retention strategies. We don't just use standard templates; our architects analyze your specific plot to engineer retaining walls and drainage systems that will protect your home for generations.
              </p>
              <p className="text-gray-600 text-lg">
                We're a few minutes away from Main Road, Iritty. This operational proximity means our site engineers are constantly monitoring your project's progress, ensuring flawless execution.
              </p>
            </div>
          </section>

          {/* Services */}
          <section className="bg-gray-50 rounded-2xl overflow-hidden p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-8 text-center">Comprehensive Construction Services in Iritty</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-4">
                  <Wrench size={24} />
                </div>
                <h3 className="text-xl font-bold mb-2">Contemporary Home Design</h3>
                <p className="text-gray-600">Fusing modern aesthetics with traditional Kerala roofing elements to match your exact taste.</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-4">
                  <Wrench size={24} />
                </div>
                <h3 className="text-xl font-bold mb-2">Zero-Headache Turnkey</h3>
                <p className="text-gray-600">Hand over the plot, and we'll hand over the keys. Complete material and labor management.</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-4">
                  <Wrench size={24} />
                </div>
                <h3 className="text-xl font-bold mb-2">Building Extensions</h3>
                <p className="text-gray-600">Adding a second floor or expanding your kitchen safely without compromising the existing structure.</p>
              </div>
            </div>
          </section>

          {/* Trust Section */}
          <section className="text-center max-w-3xl mx-auto space-y-6">
            <h2 className="text-3xl font-bold">Why Iritty Residents Trust Us</h2>
            <p className="text-xl text-gray-600">
              Our headquarters is proudly positioned in Iritty, meaning we are accountable to our direct community. We have over 10 years of spotless track record, utilizing top-grade TMT steel, premium cement, and highly skilled carpenters and masons to ensure that "Master Home" means master quality.
            </p>
          </section>

          {/* CTA */}
          <section className="bg-gray-900 rounded-2xl p-12 text-center text-white shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-900/40 to-transparent"></div>
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-4">Ready to Build in Iritty?</h2>
              <p className="text-xl mb-8 text-gray-300">Contact us today to receive a transparent, no-obligation construction quote.</p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a href="tel:+919567810944" className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 text-white rounded-full font-bold text-lg hover:bg-primary-500 transition-colors shadow-lg">
                  <Phone className="mr-2" /> Call +91 9567810944
                </a>
                <a href="https://wa.me/919567810944" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center px-8 py-4 text-white border-2 border-gray-600 rounded-full font-bold text-lg hover:border-white transition-colors">
                  Message on WhatsApp
                </a>
              </div>
            </div>
          </section>
        </div>
      </PageLayout>
    </>
  );
};

export default IrittyLanding;
